import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Row, Col, Select, DatePicker, Input, Button, ConfigProvider, Popover } from 'antd';
import { DownloadOutlined, FileSearchOutlined, SearchOutlined, TeamOutlined, DownOutlined } from '@ant-design/icons';
import { PdfIcon, CalendarIcon } from '../../assets';
import dayjs from 'dayjs';
import styles from "./index.module.css";
import { reportTheme } from './reportTheme';
import { USER_ROLES } from '../../constants/constants';
import { GroupedCheckboxFilterMenu } from '../common/FilterPanel/FilterComponents/GroupedCheckboxFilterMenu/GroupedCheckboxFilterMenu';
import menuStyles from '../common/FilterPanel/FilterComponents/GroupedCheckboxFilterMenu/OrganizationDropdown.module.css';
const { RangePicker } = DatePicker;

export const ReportsSection = ({ organizations, transportCompanies, dateRange: _dateRange, exportReport,
  selHoOrgId, userRole, loading, organizationSections }) => {

  const [selectedReport, setSelectedReport] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [orgMultiPopoverOpen, setOrgMultiPopoverOpen] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const downloadBarRef = useRef(null);

  const orgnaization = useMemo(
    () => [
      { value: 'all', label: 'All Healthcare Systems' },
      ...(organizations?.map((org) => ({ label: org.name, value: org.id })) ?? []),
    ],
    [organizations],
  );

  const orgMultiSelectParam = { name: 'organizationIds', label: 'Organization', type: 'orgGroupedMultiSelect', defaultValue: [] };
  const tcData = useMemo(
    () => [
      { value: 'all', label: 'All Transport Companies' },
      ...(transportCompanies?.map((org) => ({ label: org.name, value: org.id })) ?? []),
    ],
    [transportCompanies],
  );

  const reports = {
    'monthly-ride-summary': {
      name: 'Ride Summary',
      description: 'Monthly summary of key ride details across the organization.',
      params: [
        { name: 'monthYear', label: 'Month & Year', type: 'monthpicker', defaultValue: dayjs(), required: true }
      ]
    },
    'ho-user-activity': {
      name: 'User Management',
      description: 'Overview of all users within the organization, including active and inactive user accounts.',
      params: []
    },
    'user-engagement': {
      name: 'User Engagement',
      description: 'Detailed report on staff activity, including monthly login activity and each user’s most recent login.',
      params: [
        // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
        orgMultiSelectParam,
        { name: 'dateRange', label: 'Date Range', type: 'daterange', required: false }
      ]
    }
  };

  const roleBasedReports = {
    [USER_ROLES.HO_ADMIN]: {
      'ho-user-activity': { params: [] },
      'user-engagement': {
        params: [
          !selHoOrgId ? orgMultiSelectParam : null,
          { name: 'dateRange', label: 'Date Range', type: 'daterange', required: false }
        ]
      },
      'ho-user-activity': { params: !selHoOrgId ? [orgMultiSelectParam] : [] }
    },
    [USER_ROLES.ENTERPRISE_ADMIN]: {
      'ho-user-activity': {
        params: [
          // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
          orgMultiSelectParam,
        ]
      },
      'user-engagement': {
        params: [
          // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
          orgMultiSelectParam,
        ]
      },
      // 'rider-feedback-report': {
      //   params: [
      //     { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true }
      //   ]
      // }
    },
    [USER_ROLES.SUPER_ADMIN]: {
      '7-min-tc-performance': {
        name: 'Ride Start Time (±7 Minutes)',
        description: 'Performance report for transport companies with 7-minute threshold',
        params: [
          { name: 'dateRange', label: 'Date Range', type: 'daterange', defaultValue: [dayjs().subtract(30, 'days'), dayjs()], required: true }
        ]
      },
      '15-min-tc-performance': {
        name: 'Ride Start Time (±15 Minutes)',
        description: 'Performance report for transport companies with 15-minute threshold',
        params: [
          { name: 'dateRange', label: 'Date Range', type: 'daterange', defaultValue: [dayjs().subtract(30, 'days'), dayjs()], required: true }
        ]
      },
      'driver-performance-report': {
        name: 'Driver Status and Performance',
        description: 'Detailed report on Driver Performance',
        params: [
          { name: 'tcId', label: 'Transportation Companies', type: 'select', options: tcData, defaultValue: 'all', required: true },
          { name: 'dateRange', label: 'Date Range', type: 'daterange', defaultValue: [dayjs().subtract(30, 'days'), dayjs()], required: true }
        ]
      },
      'driver-rides-arrival-time': {
        name: 'On-Time Driver Arrivals',
        description: 'Detailed report on driver rides and their arrival times',
        params: [
          { name: 'monthYear', label: 'Month & Year', type: 'monthpicker', defaultValue: dayjs(), required: true }
        ]
      },
      'overall-tc-performance': {
        name: 'TC Active/Inactive Status',
        description: 'Detailed report on user activity for Health Organization',
        params: [
          { name: 'tcId', label: 'Transportation Companies', type: 'select', options: tcData, defaultValue: 'all', required: true },
          { name: 'dateRange', label: 'Date Range', type: 'daterange', defaultValue: [dayjs().subtract(30, 'days'), dayjs()], required: true }
        ]
      },
      'ho-user-activity': {
        params: [
          // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
          orgMultiSelectParam,
        ]
      },
      'rider-feedback-report': {
        name: 'Rider Feedback',
        description: 'Detailed summary of rider provided feedback',
        params: [
          // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
          orgMultiSelectParam,
          { name: 'dateRange', label: 'Date Range', type: 'daterange', required: false }
        ]
      },
      'unfulfilled-transition-report': {
        name: 'Unfulfilled Ride Status Transition',
        description: 'Detailed report on unfulfilled ride status transitions',
        params: [
          // { name: 'organizationId', label: 'Organization', type: 'select', options: orgnaization, defaultValue: 'all', required: true },
          orgMultiSelectParam,
          { name: 'dateRange', label: 'Date Range', type: 'daterange', required: false }
        ]
      }
    }
  };

  const reportDisplayOrder = [
    '7-min-tc-performance',
    '15-min-tc-performance',
    'user-engagement',
    'ho-user-activity',
    'monthly-ride-summary',
    'rider-feedback-report',
    'driver-performance-report',
    'driver-rides-arrival-time',
    'overall-tc-performance',
    'unfulfilled-transition-report',
  ];

  const getUserReports = (role) => {
    const roleBased = roleBasedReports[role] || {};
    const merged = Object.entries(reports).map(([id, report]) => {
      const override = roleBased[id] || {};
      return { id, ...report, ...override, params: override.params ?? report.params };
    });

    const extraReports = Object.entries(roleBased)
      .filter(([id]) => !reports[id])
      .map(([id, rpt]) => ({ id, ...rpt }));

    const userReports = [...merged, ...extraReports];

    return userReports.sort((a, b) => {
      const indexA = reportDisplayOrder.indexOf(a.id);
      const indexB = reportDisplayOrder.indexOf(b.id);
      const safeIndexA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeIndexB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return safeIndexA - safeIndexB;
    });
  };

  const filteredReports = getUserReports(userRole).filter(report =>
    report.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleParamChange = (paramName, value) => {
    setParamValues(prev => {
      if (paramName === 'dateRange') {
        return { ...prev, [paramName]: value && value.length === 2 ? [value[0], value[1]] : null };
      }

      return { ...prev, [paramName]: value };
    });
  };

  const handleTileClick = (report) => {
    const defaults = {};
    report.params?.forEach(param => {
      if (param) {
        if (param.type === 'daterange' && Array.isArray(param.defaultValue) && param.defaultValue.length === 2) {
          defaults[param.name] = [param.defaultValue[0], param.defaultValue[1]];
        } else if (param.type === 'orgGroupedMultiSelect') {
          defaults[param.name] = [];
        } else {
          defaults[param.name] = param.defaultValue;
        }
      }
    });
    setParamValues(defaults);
    setOrgMultiPopoverOpen({});
    setSelectedReport(report);
  };

  useEffect(() => {
    if (!selectedReport) return undefined;

    const handleOutsideClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (downloadBarRef.current?.contains(target)) return;
      if (target.closest('[data-report-tile="true"]')) return;
      /* Select / DatePicker / RangePicker / Popover render overlays on document.body */
      if (target.closest('.ant-select-dropdown')) return;
      if (target.closest('.ant-picker-dropdown')) return;
      if (target.closest('.ant-popover')) return;

      setSelectedReport(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [selectedReport]);

  const handleDownload = () => {
    if (!selectedReport) return;

    const params = { ...paramValues };

    selectedReport.params?.forEach(param => {
      if (param) {
      if (param.type === 'monthpicker' && params[param.name]) {
        params[param.name] = {
          month: params[param.name].month() + 1,
          year: params[param.name].year(),
        };
      } else if (param.type === 'daterange' && params[param.name]) {
        params[param.name] = {
          startDate: params[param.name]?.[0] ? params[param.name][0].format('YYYY-MM-DD') : null,
          endDate: params[param.name]?.[1] ? params[param.name][1].format('YYYY-MM-DD') : null,
        };
      } else if (param.type === 'orgGroupedMultiSelect' && Array.isArray(params[param.name]) && params[param.name].length > 0) {
        params[param.name] = params[param.name].map(o => o.id).join(',');
      } else if (param.type === 'orgGroupedMultiSelect') {
        if (userRole === USER_ROLES.ENTERPRISE_ADMIN || (!selHoOrgId && userRole === USER_ROLES.HO_ADMIN)) {
          // sending all the HO so the backend doesn't return everything
          const allOrgIds = (organizationSections ?? [])
            .flatMap(section => section.items ?? [])
            .map(item => item.value?.id)
            .filter(Boolean);
          
          if (allOrgIds.length > 0) {
            params[param.name] = allOrgIds.join(',');
          } else {
            delete params[param.name];
          }
        } else {
          delete params[param.name];
        }
      }}
    });

    const orgId = Array.isArray(selHoOrgId) ? selHoOrgId[0] : selHoOrgId;
    
    if (selectedReport.id === 'monthly-ride-summary' && userRole === USER_ROLES.ENTERPRISE_ADMIN) {
      const allOrgIds = (organizationSections ?? [])
            .flatMap(section => section.items ?? [])
            .map(item => item.value?.id)
            .filter(Boolean);
          
      params['organizationIds'] = allOrgIds.join(',');
    } else
    if (orgId && orgId !== 'all' && !params['organizationIds']) {
      params['organizationId'] = orgId;
    }

    exportReport({ id: selectedReport.id, params });
  };

  // const hasSingleOrgParam = selectedReport?.params?.some(p => p.name === 'organizationId');
  // const hasMultiOrgParam = selectedReport?.params?.some(p => p.name === 'organizationIds');

  const renderParamControl = (param) => {
    if (param?.type === 'orgGroupedMultiSelect') {
      const selectedItems = paramValues[param?.name] ?? [];
      const isOpen = !!orgMultiPopoverOpen[param?.name];
      const sections = organizationSections ?? [];
      return (
        <Popover
          open={isOpen}
          onOpenChange={(open) => setOrgMultiPopoverOpen(prev => ({ ...prev, [param?.name]: open }))}
          trigger="click"
          placement="top"
          classNames={{ root: styles.reportOrgMultiPopoverOverlay }}
          content={
            <div data-nested-checkbox-filter-root data-report-org-filter onMouseDown={e => e.stopPropagation()}>
              <GroupedCheckboxFilterMenu
                sections={sections}
                selectedItems={selectedItems}
                onChange={(items) => handleParamChange(param?.name, items)}
                fieldName="reportOrganizations"
                checkboxClassName={menuStyles.menuCheckbox}
              />
            </div>
          }
        >
          <div className={styles.reportOrgMultiTrigger}>
            <div className={styles.reportOrgMultiTriggerContent}>
              {selectedItems.length === 0 ? (
                <span className={styles.reportOrgMultiPlaceholder}>All Healthcare Systems</span>
              ) : (
                <>
                  {selectedItems.slice(0, 1).map(item => (
                    <span key={item.id} className={styles.reportOrgMultiTag}>
                      <span className={styles.reportOrgMultiTagInner}>{item.name}</span>
                      <span
                        className={styles.reportOrgMultiTagClose}
                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={e => {
                          e.stopPropagation();
                          handleParamChange(param?.name, selectedItems.filter(o => o.id !== item.id));
                        }}
                      >×</span>
                    </span>
                  ))}
                  {selectedItems.length > 1 && (
                    <span className={styles.reportOrgMultiTag}>+{selectedItems.length - 1}...</span>
                  )}
                </>
              )}
            </div>
            <DownOutlined className={styles.reportOrgMultiChevron} />
          </div>
        </Popover>
      );
    }
    if (param?.type === 'select') {
      return (
        <Select
          size="small"
          variant="borderless"
          className={styles.paramControlSelect}
          classNames={{ popup: { root: styles.downloadBarSelectDropdown } }}
          value={paramValues[param?.name] ?? param?.defaultValue}
          onChange={value => handleParamChange(param?.name, value)}
          options={param?.options}
          popupMatchSelectWidth={false}
          suffixIcon={<DownOutlined />}
        />
      );
    }
    if (param?.type === 'monthpicker') {
      return (
        <DatePicker
          size="small"
          picker="month"
          variant="borderless"
          className={styles.paramControlMonthPicker}
          value={paramValues[param?.name] ? dayjs(paramValues[param?.name]) : null}
          onChange={date => handleParamChange(param?.name, date)}
          format="MMMM YYYY"
        />
      );
    }
    if (param?.type === 'daterange') {
      return (
        <RangePicker
          size="small"
          variant="borderless"
          className={styles.paramControlRangePicker}
          classNames={{ popup: { root: styles.reportDateRangeDropdown } }}
          value={paramValues[param?.name] ? [
            dayjs(paramValues[param?.name][0]),
            dayjs(paramValues[param?.name][1])
          ] : null}
          onChange={dates => handleParamChange(param?.name, dates ? [dates[0], dates[1]] : null)}
          format="MMM D, YYYY"
          separator=" - "
          allowClear={false}
          suffixIcon={<DownOutlined />}
        />
      );
    }
    return (
      <Input
        size="small"
        variant="borderless"
        className={styles.paramControlInput}
        value={paramValues[param?.name] || ''}
        onChange={e => handleParamChange(param?.name, e.target.value)}
      />
    );
  };

  const getParamIcon = (paramType) => {
    if (paramType === 'select' || paramType === 'orgGroupedMultiSelect') return <TeamOutlined className={styles.downloadBarParamIcon} />;
    if (paramType === 'daterange' || paramType === 'monthpicker') {
      return <CalendarIcon className={styles.downloadBarParamIcon} width={16} height={16} stroke="#047857" />;
    }
    return null;
  };

  const hasOrganizationParam = selectedReport?.params?.some(
    (param) => param?.name === 'organizationId' || param?.label?.toLowerCase() === 'organization'
  );
  const hasDateRangeParam = selectedReport?.params?.some(
    (param) => param?.name === 'dateRange' || param?.label?.toLowerCase() === 'date range'
  );
  const isOrganizationNoDateRange = selectedReport && hasOrganizationParam && !hasDateRangeParam;
  const isMultiFilterReport = (selectedReport?.params?.length || 0) > 1;

  const isInDropdownLabelParam = (param) =>
    ['organizationId', 'organizationIds', 'tcId', 'dateRange', 'monthYear'].includes(param?.name);

  return (
    <ConfigProvider theme={reportTheme}>
      <Card
        loading={loading}
        title={<span className={styles.cardTitle}>Download Reports</span>}
        extra={
          <Input
            placeholder="Search"
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined className={styles.searchIcon} />}
            className={styles.searchInput}
          />
        }
        className={`${styles.reportsCard}${selectedReport ? ` ${styles.reportsCardWithBar}` : ''}`}
      >
        <Row gutter={[16, 16]}>
          {filteredReports.length > 0 ? (
            filteredReports.map(report => (
              <Col key={report.id} xs={24} sm={24} md={12} lg={8} xl={8}>
                <Card
                  hoverable
                  data-report-tile="true"
                  className={`${styles.reportTile} ${selectedReport?.id === report.id ? styles.reportTileSelected : ''}`}
                  onClick={() => handleTileClick(report)}
                >
                  <div className={styles.reportTileContent}>
                    <div className={styles.reportTileIcon}>
                      <PdfIcon fill="#315E45" width={20} height={26} />
                    </div>
                    <div className={styles.reportTileText}>
                      <div className={styles.reportTileName}>
                        {report.name}
                      </div>
                      <div className={styles.reportTileDescription}>
                        {report.description}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={24}>
              <div className={styles.noResultsContainer}>
                <FileSearchOutlined className={styles.noResultsIcon} />
                <div>No reports match your search</div>
              </div>
            </Col>
          )}
        </Row>

      </Card>

      {/* Bottom sheet — slides up from bottom of screen */}
      <div
        ref={downloadBarRef}
        className={`${styles.downloadBar} ${selectedReport ? styles.downloadBarVisible : ''} ${selectedReport && !hasOrganizationParam ? styles.downloadBarNoOrganization : ''} ${isOrganizationNoDateRange ? styles.downloadBarOrgNoDateRange : ''} ${isMultiFilterReport ? styles.downloadBarMultiFilter : ''}`}
      >
        {/* Report identity */}
        <div className={styles.downloadBarInfo}>
          <div className={styles.reportTileIcon}>
            <PdfIcon fill="#315E45" width={18} height={24} />
          </div>
          <div className={styles.downloadBarInfoText}>
            <div className={styles.downloadBarLabel}>Download Report</div>
            <div className={styles.downloadBarName} title={selectedReport?.name}>
              {selectedReport?.name}
            </div>
          </div>
        </div>

        {/* Param sections */}
        {selectedReport?.params?.filter((param) =>
          !(param?.name === 'organizationId' && (organizations?.length ?? 0) <= 1)
        ).map((param) => {
          const paramIcon = getParamIcon(param?.type);
          return paramIcon && (
            <div className={styles.downloadBarParam} key={param?.name}>
              <div className={`${styles.downloadBarParamValue} ${styles.downloadBarParamValueBox}`}>
                <div className={styles.inDropdownParamRow}>
                  {paramIcon ? (
                    <div className={styles.inDropdownParamIconWrap} aria-hidden>
                      {paramIcon}
                    </div>
                  ) : null}
                  <div className={styles.inDropdownParamTextStack}>
                    {isInDropdownLabelParam(param) && (
                      <div className={styles.inDropdownParamLabel}>{param?.label}</div>
                    )}
                    <div className={styles.inDropdownParamControlOnly}>
                      {renderParamControl(param)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Download button(s) */}
        <div className={styles.downloadBarAction}>
          {/* hasSingleOrgParam && hasMultiOrgParam ? (
            <>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload('single')}
                className={styles.downloadBarBtn}
              >
                Download (Org)
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload('multi')}
                className={styles.downloadBarBtn}
              >
                Download (Multi)
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload()}
              className={styles.downloadBarBtn}
            >
              Download
            </Button>
          ) */}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload()}
            className={styles.downloadBarBtn}
          >
            Download
          </Button>
        </div>
      </div>
    </ConfigProvider>
  );
};
